import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type DeliveryDto,
  type ShareProjectRequest,
  type ShareProjectResponse,
} from '../lib/api';
import { Button, Card, ErrorAlert, Field, TextInput } from '../lib/ui';

export function ProjectShareCard({ projectId }: { projectId: number }) {
  const qc = useQueryClient();
  const [sendToAccount, setSendToAccount] = useState(true);
  const [sendToAds, setSendToAds] = useState(false);
  const [optInNewsletter, setOptInNewsletter] = useState(false);
  const [tosAgree, setTosAgree] = useState(false);
  const [recipients, setRecipients] = useState<string[]>(['']);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const history = useQuery({
    queryKey: ['project-deliveries', projectId],
    queryFn: () => api.get<DeliveryDto[]>(`/api/v1/project-folders/${projectId}/deliveries`),
  });

  const share = useMutation({
    mutationFn: (body: ShareProjectRequest) =>
      api.post<ShareProjectResponse>(`/api/v1/project-folders/${projectId}/share`, body),
    onSuccess: (res) => {
      setMessage(res.message || 'Project shared.');
      setRecipients(['']);
      qc.invalidateQueries({ queryKey: ['project-deliveries', projectId] });
      qc.invalidateQueries({ queryKey: ['project-folder', projectId] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Share failed'),
  });

  const submit = () => {
    setError(null);
    setMessage(null);
    if (!tosAgree) {
      setError('Please agree to the Terms & Conditions before submitting your project.');
      return;
    }
    const cleaned = recipients.map((r) => r.trim()).filter(Boolean);
    if (!sendToAccount && !sendToAds && cleaned.length === 0) {
      setError('Pick at least one destination (your account, ADS, or a recipient).');
      return;
    }
    share.mutate({ sendToAccount, sendToAds, recipients: cleaned, optInNewsletter });
  };

  return (
    <Card title="Share or Submit Project" actions={
      <Button disabled={share.isPending} onClick={submit}>
        {share.isPending ? 'Submitting…' : 'Submit'}
      </Button>
    }>
      <div className="space-y-4">
        <div className="space-y-2">
          <CheckRow checked={sendToAccount} onChange={setSendToAccount}
            label="Send to my account email" />
          <CheckRow checked={sendToAds} onChange={setSendToAds}
            label="Send to ADS sales contact"
            hint="Marks the project complete and requests sales follow-up." />
          <CheckRow checked={optInNewsletter} onChange={setOptInNewsletter}
            label="Sign me up to receive the latest news and product information." />
        </div>

        <label className="flex items-start gap-2 border-t border-slate-200 pt-4 text-sm">
          <input
            type="checkbox"
            checked={tosAgree}
            onChange={(e) => setTosAgree(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-slate-800">
            I agree to the{' '}
            <a
              href="https://www.adspipe.com/terms-conditions/terms-and-conditions-sales"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-lime-600 hover:text-lime-700"
            >
              Terms &amp; Conditions
            </a>
          </span>
        </label>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Additional recipients
          </p>
          <div className="space-y-2">
            {recipients.map((r, i) => (
              <div key={i} className="flex gap-2">
                <Field label="" className="flex-1">
                  <TextInput
                    type="email"
                    placeholder="name@example.com"
                    value={r}
                    onChange={(e) => setRecipients((arr) => arr.map((v, idx) => idx === i ? e.target.value : v))}
                  />
                </Field>
                {recipients.length > 1 && (
                  <Button variant="secondary"
                    onClick={() => setRecipients((arr) => arr.filter((_, idx) => idx !== i))}>
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button variant="secondary" onClick={() => setRecipients((arr) => [...arr, ''])}>
              + Add recipient
            </Button>
          </div>
        </div>

        {message && (
          <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {message}
          </p>
        )}
        <ErrorAlert message={error} />

        <details>
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500">
            Share history ({history.data?.length ?? 0})
          </summary>
          <ul className="mt-2 divide-y divide-slate-100 text-sm">
            {(history.data ?? []).map((d) => (
              <li key={d.id} className="flex items-center justify-between py-1.5">
                <span className="text-slate-700">{d.email}</span>
                <span className="flex items-center gap-2 text-xs text-slate-500">
                  <span>{d.createdAt ? new Date(d.createdAt).toLocaleString() : '—'}</span>
                  <span className={d.emailDelivered ? 'text-emerald-700' : 'text-amber-700'}>
                    {d.emailDelivered ? 'delivered' : 'pending'}
                  </span>
                </span>
              </li>
            ))}
            {(history.data?.length ?? 0) === 0 && (
              <li className="py-2 text-xs text-slate-500">No previous shares.</li>
            )}
          </ul>
        </details>
      </div>
    </Card>
  );
}

function CheckRow({ checked, onChange, label, hint }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
      <span>
        <span className="text-slate-800">{label}</span>
        {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      </span>
    </label>
  );
}
